import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { LogOut, Eye } from 'lucide-react';

interface HeaderProps {
  title: string;
}

export const Header: React.FC<HeaderProps> = ({ title }) => {
  const { user, role, logout, impersonation, stopImpersonation } = useAuth();
  const { isConnected } = useWebSocket();

  return (
    <>
      {/* Impersonation Banner */}
      {impersonation.isImpersonating && (
        <div
          style={{
            background: 'linear-gradient(90deg, #9333ea 0%, #7e22ce 100%)',
            color: '#ffffff',
            padding: '8px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12.5px',
            fontWeight: 500,
            zIndex: 50,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Eye size={16} />
            <span>
              <strong>Read-Only Impersonation Active:</strong> Viewing console as customer{' '}
              <u>{impersonation.customerName || impersonation.customerId}</u>. Audited time-boxed session.
            </span>
          </div>
          <button
            onClick={stopImpersonation}
            className="btn btn-sm"
            style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none' }}
          >
            Exit Customer View
          </button>
        </div>
      )}

      <header className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-main)', letterSpacing: '-0.01em' }}>
            {title}
          </h1>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Live WebSocket Real-time Status */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '11.5px',
              color: isConnected ? 'var(--success-text)' : 'var(--danger-text)',
              background: isConnected ? 'var(--success-bg)' : 'var(--danger-bg)',
              padding: '4px 10px',
              borderRadius: '9999px',
              border: `1px solid ${isConnected ? 'var(--success-border)' : 'var(--danger-border)'}`,
            }}
            title="Real-time WebSocket connection to /v2/notifications/ws stream"
          >
            <span className={`badge-dot ${isConnected ? 'pulse' : ''}`} style={{ background: isConnected ? 'var(--success)' : 'var(--danger)' }} />
            <span>{isConnected ? 'Real-Time Stream Active' : 'Connecting...'}</span>
          </div>

          {/* The operator's role comes from their account (read-only). */}
          <div className="role-pill" title="Your account role">
            <span style={{ fontSize: '11px', color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>
              {role.replace(/_/g, ' ')}
            </span>
          </div>

          {/* Admin Profile & Logout */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-main)' }}>
                {user?.name.split(' ')[0]}
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{user?.email}</div>
            </div>
            <button
              onClick={logout}
              className="btn btn-ghost btn-sm"
              title="Sign Out"
              aria-label="Sign Out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
    </>
  );
};
